// Client-error pipeline. Collects errors from five sources: `window` "error"
// (`onerror`), `unhandledrejection`, the app log facade (`reportClientError`,
// kind="log"), Pusher connection-health signals (`reportPusherHealth`,
// kind="pusher"), and failed API fetches (`reportFetchFailure`, kind="fetch").
// rum.ts flushes the one bounded queue to /api/client-errors on a short timer and
// on visibility/pagehide.
//
// Bounds (an error storm must never DoS the ingest or the browser):
//  - dedupe by hash(message + source + line) for the whole session (fetch
//    failures skip dedupe because their rate is the signal),
//  - at most 20 errors per flushed batch,
//  - at most 100 errors per session.
//
// Every field is truncated to the backend's max_length so one oversize error can
// never 422 the entire batch (the backend rejects the whole body on any overflow).
// The pipeline is DORMANT until initRum enables it (a sampled-out page reports
// nothing). `reportClientError` is then a no-op, so callers need no guard.

import type { ClientError } from "./types";

const MAX_PER_BATCH = 20;
const MAX_PER_SESSION = 100;
const MESSAGE_MAX = 1024; // ClientError.message
const STACK_MAX = 4096; // ClientError.stack
const SOURCE_MAX = 256; // ClientError.source

let enabled = false;
let queue: ClientError[] = [];
let seen = new Set<string>();
let sessionCount = 0;
// rum.ts registers its (debounced) flusher here so an enqueued error reaches the
// backend within seconds of happening, not only at visibilitychange/pagehide. A
// tab mid-incident (the exact moment fetch failures fire) may never background,
// and the whole point of the fetch-failure beacon is seeing the failure LIVE.
let scheduleFlush: (() => void) | null = null;

/** rum.ts wires its debounced error flush here (null on teardown). INTERNAL. */
export function setErrorFlushScheduler(fn: (() => void) | null): void {
  scheduleFlush = fn;
}

/** Flip the pipeline on/off. initRum enables it only when the page is sampled in. */
export function setErrorsEnabled(on: boolean): void {
  enabled = on;
}

function truncate(value: string, max: number): string {
  return value.length > max ? value.slice(0, max) : value;
}

/** Stable session-dedupe key: hash(message + source + line) (djb2, unsigned). */
function hashKey(
  message: string,
  source: string | null | undefined,
  line: number | null | undefined,
): string {
  const basis = `${message}\u0000${source ?? ""}\u0000${line ?? ""}`;
  let h = 5381;
  for (let i = 0; i < basis.length; i += 1) {
    h = (Math.imul(h, 33) + basis.charCodeAt(i)) | 0;
  }
  return (h >>> 0).toString(36);
}

/** Enqueue one error, applying dedupe + both caps. INTERNAL. */
function enqueue(error: ClientError, opts: { skipDedupe?: boolean } = {}): void {
  if (!enabled) return;
  const key = hashKey(error.message, error.source, error.line);
  // `skipDedupe`: a fetch failure repeating IS the signal (its rate distinguishes
  // a blip from an outage), so identical repeats must keep counting — the batch +
  // session caps still bound a storm.
  if (!opts.skipDedupe && seen.has(key)) return; // already reported this session
  if (sessionCount >= MAX_PER_SESSION) return; // session budget spent
  if (queue.length >= MAX_PER_BATCH) return; // batch full — dropped until next flush
  seen.add(key);
  sessionCount += 1;
  queue.push(error);
  scheduleFlush?.();
}

function safeStringify(data: Record<string, unknown>): string {
  try {
    return JSON.stringify(data);
  } catch {
    return "[unserializable]";
  }
}

/**
 * Report a client-side error / diagnostic entry (kind="log"). This is the diag
 * sink the app log facade forwards `error()` calls to, so client crashes surface
 * in Cloud Logging. Signature matches `log.error`: (scope, message, data?, error?).
 * A no-op until RUM is initialized + sampled in.
 */
export function reportClientError(
  scope: string,
  message: string,
  data?: Record<string, unknown>,
  error?: unknown,
  // `skipDedupe`: for a report whose REPETITION is the signal (see `enqueue`).
  // Default off: a crash repeating is noise, a drop repeating is not.
  opts: { skipDedupe?: boolean } = {},
): void {
  if (!enabled) return;
  const hasData = data != null && Object.keys(data).length > 0;
  const composed = hasData
    ? `[${scope}] ${message} ${safeStringify(data)}`
    : `[${scope}] ${message}`;
  const stack = error instanceof Error && error.stack ? error.stack : undefined;
  enqueue(
    {
      kind: "log",
      message: truncate(composed, MESSAGE_MAX),
      stack: stack != null ? truncate(stack, STACK_MAX) : null,
      source: null,
      line: null,
      col: null,
    },
    opts,
  );
}

/**
 * Report one failed same-origin API fetch (kind="fetch") — a request that never
 * produced an HTTP response: a network drop ("Failed to fetch"), a transport
 * stall cut by the caller's deadline (abort), or a DNS/TLS/edge failure. These
 * failures are INVISIBLE to server logs (the request never arrived), so this
 * beacon is the only measurement of them; the backend logs one WARNING
 * `client_error` line per entry and the `client_fetch_failure` log-based metric
 * counts them. `path` must carry NO query string (capability tokens / ids ride
 * there — #593) — callers strip it. Repeats are NOT session-deduped: the rate is
 * the signal.
 */
export function reportFetchFailure(input: {
  /** Request path with the query string already stripped, e.g. "/api/workspace". */
  path: string;
  method: string;
  /** Bounded failure taxonomy + the raw error text, e.g. "timeout" or "network". */
  reason: string;
  durationMs: number;
}): void {
  if (!enabled) return;
  const message = `fetch-failed ${input.method} ${input.path} reason=${input.reason} after=${Math.round(input.durationMs)}ms`;
  enqueue(
    {
      kind: "fetch",
      message: truncate(message, MESSAGE_MAX),
      stack: null,
      source: truncate(input.path, SOURCE_MAX),
      line: null,
      col: null,
    },
    { skipDedupe: true },
  );
}

/**
 * Report a Pusher connection-health signal (kind="pusher"). Wired as the consumer
 * of the pusher manager's inverted health callback (`setPusherHealthHandler`) so
 * the manager never imports rum. `event.kind` is a `PusherHealthEvent` sub-kind.
 */
export function reportPusherHealth(event: { kind: string; detail?: string }): void {
  if (!enabled) return;
  const detail = event.detail != null && event.detail !== "" ? ` ${event.detail}` : "";
  enqueue({
    kind: "pusher",
    message: truncate(`pusher:${event.kind}${detail}`, MESSAGE_MAX),
    stack: null,
    source: null,
    line: null,
    col: null,
  });
}

/**
 * Install the global `error` + `unhandledrejection` listeners. Returns a remover.
 * (admin has no log facade, so these handlers are its only error source.)
 */
export function installGlobalErrorHandlers(): () => void {
  const onError = (event: ErrorEvent): void => {
    enqueue({
      kind: "onerror",
      message: truncate(event.message || "uncaught error", MESSAGE_MAX),
      stack:
        event.error instanceof Error && event.error.stack
          ? truncate(event.error.stack, STACK_MAX)
          : null,
      source: event.filename ? truncate(event.filename, SOURCE_MAX) : null,
      line: event.lineno ?? null,
      col: event.colno ?? null,
    });
  };
  const onRejection = (event: PromiseRejectionEvent): void => {
    const reason: unknown = event.reason;
    const message = reason instanceof Error ? reason.message : String(reason);
    enqueue({
      kind: "unhandledrejection",
      message: truncate(message || "unhandled rejection", MESSAGE_MAX),
      stack: reason instanceof Error && reason.stack ? truncate(reason.stack, STACK_MAX) : null,
      source: null,
      line: null,
      col: null,
    });
  };
  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onRejection);
  return (): void => {
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onRejection);
  };
}

/** Take (and clear) the current error batch — at most `MAX_PER_BATCH` items. */
export function drainErrors(): ClientError[] {
  if (queue.length === 0) return [];
  const batch = queue;
  queue = [];
  return batch;
}

/** Current queued (un-flushed) error count — test/introspection aid. */
export function errorQueueSize(): number {
  return queue.length;
}

/** Test-only: wipe pipeline state (queue, dedupe set, session budget, enabled). */
export function __resetErrors(): void {
  enabled = false;
  queue = [];
  seen = new Set();
  sessionCount = 0;
  scheduleFlush = null;
}
